import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  OPEN_CONSENT_EVENT,
  acceptAll,
  getConsent,
  hasAnswered,
  rejectAll,
  setConsent,
} from "../lib/consent";

/**
 * First-visit cookie banner + preferences panel.
 * Accept / Reject have equal visual weight; Customize opens category toggles.
 */
export default function CookieBanner() {
  const titleId = useId();
  const panelTitleId = useId();
  // Eager first paint — do not wait on useEffect (matches Glowkit CMP).
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return !hasAnswered();
  });
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const dialogRef = useRef(null);

  const syncFromStored = useCallback(() => {
    const stored = getConsent();
    if (stored) {
      setAnalytics(stored.analytics);
      setMarketing(stored.marketing);
    } else {
      setAnalytics(false);
      setMarketing(false);
    }
  }, []);

  useEffect(() => {
    if (!hasAnswered()) {
      setVisible(true);
    }
    const onOpen = () => {
      syncFromStored();
      setPrefsOpen(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, onOpen);
  }, [syncFromStored]);

  useEffect(() => {
    if (!visible || !dialogRef.current) return undefined;
    const node = dialogRef.current;
    const focusSelector =
      'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    const firstFocusable = node.querySelector(focusSelector);
    firstFocusable?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (prefsOpen && hasAnswered()) {
          setPrefsOpen(false);
          setVisible(false);
        }
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = node.querySelectorAll(focusSelector);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, prefsOpen]);

  if (!visible) return null;

  const closeAfterChoice = () => {
    setPrefsOpen(false);
    setVisible(false);
  };

  const onAccept = () => {
    acceptAll();
    closeAfterChoice();
  };

  const onReject = () => {
    rejectAll();
    closeAfterChoice();
  };

  const onSavePrefs = () => {
    setConsent({ analytics, marketing });
    closeAfterChoice();
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[200] p-4 md:p-6 pointer-events-none"
      data-testid="cookie-banner"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={prefsOpen ? panelTitleId : titleId}
        className="pointer-events-auto mx-auto max-w-3xl border border-wood/50 bg-warm-white shadow-[0_-8px_40px_rgba(44,46,43,0.08)] cookie-banner-panel"
      >
        {!prefsOpen ? (
          <div className="p-6 md:p-8">
            <p
              id={titleId}
              className="font-display text-2xl md:text-3xl text-stone2-dark leading-tight"
            >
              Cookie e preferenze
            </p>
            <p className="mt-3 text-sm md:text-base text-stone2-mid leading-relaxed">
              Usiamo solo cookie tecnici necessari al funzionamento del sito. Eventuali
              strumenti analitici o di marketing (se attivati in futuro) restano spenti
              finché non scegli.{" "}
              <Link to="/cookie" className="link-under text-sage-ink">
                Cookie policy
              </Link>
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onAccept}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium bg-sage-ink text-warm-white hover:bg-stone2-dark transition-colors"
                data-testid="cookie-accept"
              >
                Accetta tutti
              </button>
              <button
                type="button"
                onClick={onReject}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium border border-stone2-dark/30 text-stone2-dark hover:border-sage-ink hover:text-sage-ink transition-colors"
                data-testid="cookie-reject"
              >
                Rifiuta non necessari
              </button>
              <button
                type="button"
                onClick={() => {
                  syncFromStored();
                  setPrefsOpen(true);
                }}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium text-sage-ink hover:underline"
                data-testid="cookie-customize"
              >
                Personalizza
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 md:p-8">
            <p
              id={panelTitleId}
              className="font-display text-2xl md:text-3xl text-stone2-dark leading-tight"
            >
              Preferenze cookie
            </p>
            <p className="mt-3 text-sm text-stone2-mid leading-relaxed">
              I cookie tecnici sono sempre attivi. Analytics e marketing restano off di
              default finché non li attivi.{" "}
              <Link to="/cookie" className="link-under text-sage-ink">
                Dettagli
              </Link>
            </p>

            <ul className="mt-6 space-y-4">
              <li className="flex items-start justify-between gap-4 border-b border-wood/40 pb-4">
                <div>
                  <p className="text-sm font-medium text-stone2-dark">Necessari</p>
                  <p className="text-xs text-stone2-mid mt-1 leading-relaxed">
                    Preferenze di consenso e funzionamento di base. Sempre attivi.
                  </p>
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-stone2-gray shrink-0 pt-1">
                  Sempre on
                </span>
              </li>
              <li className="flex items-start justify-between gap-4 border-b border-wood/40 pb-4">
                <div>
                  <p className="text-sm font-medium text-stone2-dark">Analytics</p>
                  <p className="text-xs text-stone2-mid mt-1 leading-relaxed">
                    Statistiche di utilizzo aggregate, solo se configurate e con consenso.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer shrink-0 pt-1">
                  <span className="sr-only">Attiva analytics</span>
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="h-5 w-5 accent-sage-ink"
                    data-testid="cookie-toggle-analytics"
                  />
                </label>
              </li>
              <li className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-stone2-dark">Marketing</p>
                  <p className="text-xs text-stone2-mid mt-1 leading-relaxed">
                    Pixel o profilazione di marketing. Al momento non attivi sul sito.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer shrink-0 pt-1">
                  <span className="sr-only">Attiva marketing</span>
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="h-5 w-5 accent-sage-ink"
                    data-testid="cookie-toggle-marketing"
                  />
                </label>
              </li>
            </ul>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onSavePrefs}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium bg-sage-ink text-warm-white hover:bg-stone2-dark transition-colors"
                data-testid="cookie-save-prefs"
              >
                Salva preferenze
              </button>
              <button
                type="button"
                onClick={onReject}
                className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium border border-stone2-dark/30 text-stone2-dark hover:border-sage-ink hover:text-sage-ink transition-colors"
              >
                Rifiuta non necessari
              </button>
              {!hasAnswered() ? (
                <button
                  type="button"
                  onClick={() => setPrefsOpen(false)}
                  className="flex-1 min-h-11 px-5 py-3 text-sm uppercase tracking-[0.16em] font-medium text-sage-ink hover:underline"
                >
                  Indietro
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
